import { z } from 'zod';
import type { FormField } from '../types';
import { getExportDateBounds, type CampaignExportResponse } from './campaign-csv';
import { getStructuredResponseAnswers } from './response-completion';
import { isOtherChoiceAnswer } from './choice-answers';
import { responseStatusConfig } from './admin-utils';

/** Supported comparisons, shared by the question controls and agent tools. */
export const exportOperators = ['eq', 'neq', 'contains', 'not_contains', 'gt', 'gte', 'lt', 'lte', 'between', 'is_empty', 'is_not_empty'] as const;
/** A typed comparison against a question's stable ID. */
export const AnswerFilterSchema = z.object({
  fieldId: z.string().min(1).max(200), operator: z.enum(exportOperators),
  value: z.union([z.string().max(2000), z.number().finite(), z.boolean()]).optional(),
  valueTo: z.union([z.string().max(2000), z.number().finite()]).optional(),
}).strict();
/** Calendar dates and status always intersect the answer condition group. */
export const CampaignExportFiltersSchema = z.object({
  startDate: z.string().max(10).optional(), endDate: z.string().max(10).optional(),
  timezone: z.string().max(100).optional(),
  status: z.enum(['pending', 'in_progress', 'completed', 'abandoned', 'low_quality', 'insufficient']).optional(),
  match: z.enum(['all', 'any']).optional(), answers: z.array(AnswerFilterSchema).max(20).optional(),
}).strict();
/** Export preferences, with inclusive calendar dates in the saved timezone. */
export type CampaignExportFilters = z.infer<typeof CampaignExportFiltersSchema>;
/** One answer predicate. */
export type AnswerFilter = z.infer<typeof AnswerFilterSchema>;
/** Question metadata exposed to both the filter editor and hollie. */
export interface ExportField {
  id: string; label: string; kind: 'number' | 'choice' | 'text' | 'boolean' | 'presence';
  operators: AnswerFilter['operator'][];
  options?: Array<{ value: string; label: string }>;
}
/** Short labels for the filter editor and saved download summary. */
export const exportOperatorLabels: Record<AnswerFilter['operator'], string> = {
  eq: 'Is', neq: 'Is not', contains: 'Contains', not_contains: 'Does not contain',
  gt: 'Greater than', gte: 'At least', lt: 'Less than', lte: 'At most', between: 'Between',
  is_empty: 'Is empty', is_not_empty: 'Is answered',
};

/** Reveal only meaningful comparisons for each campaign question. */
export function getExportFields(fields: FormField[]): ExportField[] {
  return fields.filter(field => field.type !== 'information').map(field => {
    const common = { id: field.id, label: field.label };
    const presence: AnswerFilter['operator'][] = ['is_empty', 'is_not_empty'];
    if (field.type === 'number') return { ...common, kind: 'number', operators: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', ...presence] };
    if (field.type === 'checkbox') return { ...common, kind: 'boolean', operators: ['eq', 'neq', ...presence] };
    if (['choice_single', 'choice_multi', 'image_choice', 'ranking'].includes(field.type)) {
      const options = 'options' in field ? field.options.map(option => ({
        value: field.type === 'image_choice' || field.type === 'ranking' ? option.id : option.value,
        label: option.label,
      })) : [];
      return { ...common, kind: 'choice', options, operators: field.type === 'choice_single' ? ['eq', 'neq', ...presence] : ['contains', 'not_contains', ...presence] };
    }
    if (['file_upload', 'calendar_booking', 'signature', 'image_click_map'].includes(field.type)) return { ...common, kind: 'presence', operators: presence };
    return { ...common, kind: 'text', operators: ['contains', 'not_contains', 'eq', 'neq', ...presence] };
  });
}

/** Validate dates, question ownership, and comparison values before any row is read. */
export function validateExportFilters(raw: unknown, fields: FormField[]): CampaignExportFilters {
  const parsed = CampaignExportFiltersSchema.safeParse(raw);
  if (!parsed.success) throw new Error('Choose valid export filters.');
  const filters = parsed.data;
  getExportDateBounds(filters);
  const catalog = getExportFields(fields);
  for (const rule of filters.answers ?? []) {
    const field = catalog.find(item => item.id === rule.fieldId);
    if (!field) throw new Error('Choose a question from this campaign.');
    if (!field.operators.includes(rule.operator)) throw new Error(`Choose a comparison for ${field.label}.`);
    if (rule.operator === 'is_empty' || rule.operator === 'is_not_empty') {
      if (rule.value !== undefined || rule.valueTo !== undefined) throw new Error('Empty-answer filters do not take a value.');
      continue;
    }
    if (field.kind === 'number') {
      if (typeof rule.value !== 'number') throw new Error(`Enter a number for ${field.label}.`);
      if (rule.operator === 'between' && (typeof rule.valueTo !== 'number' || rule.valueTo < rule.value)) throw new Error('Enter a valid number range.');
    } else if (field.kind === 'boolean') {
      if (typeof rule.value !== 'boolean') throw new Error(`Choose a value for ${field.label}.`);
    } else if (typeof rule.value !== 'string' || !rule.value.trim()) throw new Error(`Enter a value for ${field.label}.`);
    if (field.kind === 'choice' && !field.options?.some(option => option.value === rule.value)) throw new Error(`Choose an option from ${field.label}.`);
    if (rule.operator !== 'between' && rule.valueTo !== undefined) throw new Error('Only ranges take a second value.');
  }
  return filters;
}

/** Compare saved answers by stable question identity, respecting historical snapshots. */
export function matchesExportAnswers(response: Pick<CampaignExportResponse, 'form_answers' | 'form_schema_snapshot' | 'calendar_bookings'>, fields: FormField[], filters: CampaignExportFilters): boolean {
  if (!filters.answers?.length) return true;
  const schema = response.form_schema_snapshot ?? fields;
  const structured = getStructuredResponseAnswers({ ...response, form_schema_snapshot: schema });
  const matches = filters.answers.map(rule => {
    const field = schema.find(item => item.id === rule.fieldId);
    const answer = structured.find(item => item.fieldId === rule.fieldId);
    let value: unknown = answer?.value;
    if (field?.type === 'file_upload') value = response.form_answers?.[field.name];
    if (field?.type === 'calendar_booking') value = response.calendar_bookings?.filter(booking => booking.fieldName === field.name);
    const present = value !== undefined && value !== null && value !== '' && (!Array.isArray(value) || value.length > 0);
    if (rule.operator === 'is_empty') return !present;
    if (rule.operator === 'is_not_empty') return present;
    if (!present || !field) return false;
    const historical = getExportFields([field])[0];
    const current = getExportFields(fields).find(item => item.id === rule.fieldId);
    if (!historical || !current || historical.kind !== current.kind || !historical.operators.includes(rule.operator)) return false;
    const kind = historical.kind;
    if (kind === 'number') {
      // Historical numeric strings are accepted only when the entire value is numeric.
      const number = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : NaN;
      if (!Number.isFinite(number) || typeof rule.value !== 'number') return false;
      switch (rule.operator) {
        case 'eq': return number === rule.value;
        case 'neq': return number !== rule.value;
        case 'gt': return number > rule.value;
        case 'gte': return number >= rule.value;
        case 'lt': return number < rule.value;
        case 'lte': return number <= rule.value;
        case 'between': return typeof rule.valueTo === 'number' && number >= rule.value && number <= rule.valueTo;
        default: return false;
      }
    }
    if (kind === 'boolean') return typeof value === 'boolean' && (rule.operator === 'eq' ? value === rule.value : value !== rule.value);
    if (kind === 'choice') {
      if (isOtherChoiceAnswer(value)) value = value.value;
      const selected = Array.isArray(value) ? value.includes(rule.value) : value === rule.value;
      return rule.operator === 'neq' || rule.operator === 'not_contains' ? !selected : selected;
    }
    const text = (answer?.displayValue ?? '').normalize('NFC').toLocaleLowerCase();
    const expected = String(rule.value).normalize('NFC').toLocaleLowerCase();
    if (rule.operator === 'eq') return text === expected;
    if (rule.operator === 'neq') return text !== expected;
    return rule.operator === 'contains' ? text.includes(expected) : !text.includes(expected);
  });
  return filters.match === 'any' ? matches.some(Boolean) : matches.every(Boolean);
}

/** A saved download action contains preferences only, never data or authorization. */
export const PreparedCampaignExportSchema = z.object({
  version: z.literal(1), id: z.string().min(1).max(100), campaignId: z.string().min(1).max(200),
  campaignName: z.string().max(1000), summary: z.string().max(20000),
  filters: CampaignExportFiltersSchema, conversationIds: z.array(z.string().min(1).max(200)).min(1).max(200).optional(),
}).strict();
/** Durable download configuration stored in the existing chat tool trace. */
export type PreparedCampaignExport = z.infer<typeof PreparedCampaignExportSchema>;

/** Prepare a validated, human-readable export; the browser reauthorizes on download. */
export function prepareCampaignExport(campaign: { id: string; name: string; form_schema?: FormField[] }, raw: unknown, conversationIds?: string[]): PreparedCampaignExport {
  const fields = campaign.form_schema ?? [];
  const filters = validateExportFilters(raw, fields);
  const catalog = getExportFields(fields);
  const summary: string[] = [];
  if (filters.status) summary.push(responseStatusConfig[filters.status].label);
  if (filters.startDate) summary.push(`From ${filters.startDate}`);
  if (filters.endDate) summary.push(`Through ${filters.endDate}`);
  if ((filters.startDate || filters.endDate) && filters.timezone) summary.push(filters.timezone);
  const conditions = (filters.answers ?? []).map(rule => {
    const field = catalog.find(item => item.id === rule.fieldId)!;
    const value = field.options?.find(option => option.value === rule.value)?.label ?? rule.value;
    return `${field.label} ${exportOperatorLabels[rule.operator].toLowerCase()}${value === undefined ? '' : ` ${value}`}${rule.operator === 'between' ? ` and ${rule.valueTo}` : ''}`;
  });
  if (conditions.length) summary.push(conditions.join(filters.match === 'any' ? ' OR ' : ' AND '));
  if (conversationIds) summary.push(`${conversationIds.length} selected conversation${conversationIds.length === 1 ? '' : 's'}`);
  return PreparedCampaignExportSchema.parse({ version: 1, id: crypto.randomUUID(), campaignId: campaign.id, campaignName: campaign.name, filters, summary: summary.join(' · ') || 'All responses', ...(conversationIds ? { conversationIds } : {}) });
}

/** Strict tool arguments; the server supplies the campaign, subset, and viewer timezone. */
export const ExportToolArgsSchema = z.object({ filters: CampaignExportFiltersSchema.omit({ timezone: true }) }).strict();
/** Gemini's JSON-schema declaration uses the same vocabulary as runtime validation. */
export const exportToolParameters = z.toJSONSchema(ExportToolArgsSchema);

/** Read a saved action only when it belongs to the visible campaign. */
export function readPreparedExport(value: unknown, campaignId: string): PreparedCampaignExport | undefined {
  const parsed = PreparedCampaignExportSchema.safeParse(value);
  return parsed.success && parsed.data.campaignId === campaignId ? parsed.data : undefined;
}
